/**
 * Editorial curation for Discover, alongside DISCOVERY_THEMES: which listings
 * the hero demos and the Featured spotlight introduce, and the short copy that
 * says why. The wide-screen hero cards are not curated; they follow popularity. Names, icons, authors and launch targets always come from the
 * listing itself; only the editorial words and the recorded media live here.
 *
 * Every entry names a stable public listing ID. Environments do not carry the
 * same catalogue, so each slot lists candidates in priority order and the UI
 * shows only those the current catalogue actually has.
 */

export interface EditorialMedia {
  webm: string;
  mp4: string;
  poster: string;
}

export interface HeroDemo {
  /** Purpose tab shown under the demo on wide screens. */
  id: "draw" | "make" | "play";
  label: string;
  listingId: string;
  /** What you do in the app, in one line. */
  useCase: string;
  /** A recording of the app alone (no ato chrome). */
  media: EditorialMedia;
}

const demoMedia = (id: HeroDemo["id"]): EditorialMedia => ({
  webm: `/landing/discover-demo-${id}.webm`,
  mp4: `/landing/discover-demo-${id}.mp4`,
  poster: `/landing/discover-demo-${id}-poster.webp`,
});

export const HERO_DEMOS: readonly HeroDemo[] = [
  { id: "draw", label: "Draw", listingId: "dapp_01M4Z5AAPG7CPC2R5E0ESB648H", useCase: "Sketch and edit diagrams", media: demoMedia("draw") },
  { id: "play", label: "Play", listingId: "discover-2048", useCase: "Slide tiles to reach 2048", media: demoMedia("play") },
];

/** How many apps the wide-screen hero shows: the most popular with a preview video. */
export const HERO_CARD_COUNT = 3;

/** Reviewed listing guidance; this does not alter execution or app identity. */
export const APP_USAGE_NOTICES: Readonly<Record<string, string>> = {
  dapp_01M4Z5AAPG7CPC2R5E0ESB648H: "Excalidraw account and Plus prompts are separate from your Ato account.",
  dapp_01M4W0ZBY0MPQH271P112F40D2: "Desktop recommended. Japanese text may not display correctly in this app.",
};

/**
 * Hero display fixes for listings whose title carries a version or whose
 * icon is a page screenshot. Which apps appear is decided by popularity.
 */
export const HERO_CARD_DISPLAY: Readonly<Record<string, { name?: string; icon?: string }>> = {
  dapp_01M4B2T2T0W38JV24KV83C4VFT: { icon: "/landing/discover-icon-minipaint.webp" },
  dapp_01M4B17Y4SYEZE5KPAVT552Q2S: { name: "BeepBox", icon: "/landing/discover-icon-beepbox.webp" },
};

export interface Spotlight {
  listingId: string;
  /** Why to try it now — not the app's name or feature list. */
  headline: string;
  summary: string;
  /** Editorial artwork; without it the listing's own preview is used. */
  media?: EditorialMedia;
}

/** The Featured row's features, in order; unavailable listings drop out. */
export const SPOTLIGHTS: readonly Spotlight[] = [
  {
    listingId: "dapp_01M4Z5AAPG7CPC2R5E0ESB648H",
    headline: "Think it through on a whiteboard.",
    summary: "Sketch hand-drawn diagrams and wireframes right in your browser, then keep editing until the idea is clear.",
  },
  {
    listingId: "discover-2048",
    headline: "Take a two-minute puzzle break.",
    summary: "Slide numbered tiles to combine them and reach 2048 — the original open-source game, right in your browser.",
  },
  {
    listingId: "dapp_01M4PVKGC4DPQPJ99GTVPTHN17",
    headline: "Make images lighter for the web.",
    summary: "Compress and resize a photo in your browser and compare the result side by side before you save it.",
  },
];

/** Every listing the editorial slots may need, in no particular order. */
export const EDITORIAL_LISTING_IDS: readonly string[] = [
  ...new Set([
    ...HERO_DEMOS.map((demo) => demo.listingId),
    ...SPOTLIGHTS.map((spotlight) => spotlight.listingId),
  ]),
];
